import React, { useEffect, useMemo } from 'react'
import { Toaster } from 'react-hot-toast'

import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'

import { ApolloProvider } from '@apollo/client'

import { CookieBanner } from '@ui/components/consent'
import { Seo } from '@ui/components/seo'
import { TooltipProvider } from '@ui/components/shadcn/ui'
import { ErrorBoundary, setAirbrakeUser } from '@ui/lib/airbrake/client'
import { honorSignal } from '@ui/lib/consent'
import { canonicalAppUrl } from '@ui/lib/urls'

import { AwsWafStageIntegration } from '@web-app/components/AwsWafStageIntegration'
import { MeasurementTags } from '@web-app/components/MeasurementTags'
import { BranchProvider } from '@web-app/context/BranchContext'
import { BrazeProvider } from '@web-app/context/BrazeContext'
import { EventTrackingProvider } from '@web-app/context/EventTrackingContext'
import { FirebaseRemoteConfigProvider } from '@web-app/context/FirebaseContext'
import { UserDataProvider } from '@web-app/context/UserDataContext'
import type { ClientSessionData } from '@web-app/hoc/withSession'
import { initializeApollo } from '@web-app/lib/apollo'
import { onSignOutElsewhere } from '@web-app/lib/auth/broadcast'
import { paytoneOne, sourceSans, sourceSerif } from '@web-app/styles/fonts'

// Load Shared Tailwind CSS
import '@ui/styles/tailwind.css'

const DEFAULT_TITLE = 'ATM.com - Earn Money - Real Cash Rewards'
const DEFAULT_DESCRIPTION =
  'Cash advances, games, surveys, rewards, and more. Get cash and earn rewards with ATM.com.'

function WebApp({ Component, pageProps }: AppProps) {
  const { asPath } = useRouter()
  // Query/hash stripped; a page's own <Seo> overrides by key.
  const canonical = canonicalAppUrl(asPath.split(/[?#]/)[0])

  // A sibling tab signed out; the shared cookie is already dead, so reload.
  useEffect(() => onSignOutElsewhere(() => window.location.reload()), [])

  useEffect(() => honorSignal(), [])

  return (
    <ErrorBoundary>
      {/* Fonts on <html>, not a wrapper, so portaled dialogs inherit them. */}
      <style jsx global>{`
        html {
          --font-primary: ${sourceSans.style.fontFamily};
          --font-serif: ${sourceSerif.style.fontFamily};
          --font-display: ${paytoneOne.style.fontFamily};
          font-family: var(--font-primary);
        }
      `}</style>
      <Seo
        title={DEFAULT_TITLE}
        description={DEFAULT_DESCRIPTION}
        canonical={canonical}
        image="/img/og/og-atm-dark-logo.png"
        imageWidth={1920}
        imageHeight={938}
      />
      <AwsWafStageIntegration />
      <AppProviders pageProps={pageProps}>
        <Component {...(pageProps as PageProps)} />
      </AppProviders>
      <Toaster position="top-center" />
      <CookieBanner />
    </ErrorBoundary>
  )
}

export interface PageProps {
  sessionData?: ClientSessionData
  [key: string]: unknown
}

interface AppProvidersProps {
  children: React.ReactNode
  pageProps: PageProps
}

const AppProviders: React.FC<AppProvidersProps> = ({ children, pageProps }) => {
  // Stamp the logged-in user's id on every Airbrake notice (userId only —
  // deliberate GLBA-minimization decision). sessionData is the sanctioned
  // server→client identity pipe; deps are re-read per notice, so this also
  // clears the stamp on logout.
  const airbrakeUserId = pageProps.sessionData?.user?.id
  useEffect(() => {
    setAirbrakeUser(airbrakeUserId != null ? String(airbrakeUserId) : null)
  }, [airbrakeUserId])

  const browserFingerprint =
    pageProps.sessionData?.browserFingerprint ?? undefined
  const sessionId = pageProps.sessionData?.sessionId ?? undefined
  const mcid = pageProps.sessionData?.mcid ?? undefined

  const userId = pageProps.sessionData?.user?.id

  // Props carry identity only; the Apollo layer fetches its own access token.
  // The client is rebuilt only when userId changes.
  const apolloClient = useMemo(
    () =>
      initializeApollo({
        browserFingerprint,
        sessionId,
        mcid,
        userId,
      }),
    [browserFingerprint, sessionId, mcid, userId],
  )

  return (
    <>
      <BranchProvider sessionData={pageProps.sessionData}>
        <BrazeProvider sessionData={pageProps.sessionData}>
          <ApolloProvider client={apolloClient}>
            <UserDataProvider sessionData={pageProps.sessionData}>
              <EventTrackingProvider sessionData={pageProps.sessionData}>
                <FirebaseRemoteConfigProvider>
                  <TooltipProvider>{children}</TooltipProvider>
                </FirebaseRemoteConfigProvider>
              </EventTrackingProvider>
            </UserDataProvider>
          </ApolloProvider>
        </BrazeProvider>
      </BranchProvider>
      <MeasurementTags userId={userId} />
    </>
  )
}

export default WebApp
